import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share, SquarePlus, Bell, ChevronRight } from 'lucide-react';

// Phone-frame illustrations for each step (pure CSS/SVG, no images to load)
const PhoneFrame = ({ children }) => (
  <div className="mx-auto w-[120px] h-[200px] rounded-[18px] border-2 border-zinc-700 bg-[#0a0a0c] relative overflow-hidden shadow-inner">
    <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1.5 rounded-full bg-zinc-800" />
    {children}
  </div>
);

const StepShare = () => (
  <PhoneFrame>
    <div className="absolute inset-x-2 top-6 space-y-1.5">
      {[0, 1, 2].map((i) => <div key={i} className="h-2 rounded bg-zinc-800" style={{ width: `${80 - i * 15}%` }} />)}
      <div className="h-14 rounded bg-zinc-800/70 mt-2" />
    </div>
    <div className="absolute bottom-0 inset-x-0 h-9 bg-zinc-900 border-t border-zinc-800 flex items-center justify-around px-2">
      <ChevronRight className="w-3 h-3 text-zinc-600 rotate-180" /><ChevronRight className="w-3 h-3 text-zinc-600" />
      <span className="relative flex items-center justify-center w-7 h-7 rounded-full bg-amber-500 ring-4 ring-amber-500/30 animate-pulse"><Share className="w-3.5 h-3.5 text-black" /></span>
      <div className="w-3 h-3 rounded-sm border border-zinc-600" /><div className="w-3 h-3 rounded-sm border border-zinc-600" />
    </div>
  </PhoneFrame>
);

const StepAdd = () => (
  <PhoneFrame>
    <div className="absolute inset-x-0 bottom-0 h-[70%] bg-zinc-900 rounded-t-xl border-t border-zinc-700 p-2 space-y-1">
      <div className="w-8 h-1 rounded-full bg-zinc-700 mx-auto mb-2" />
      {['Copy', 'Add to Reading List', 'Add Bookmark'].map((t) => (
        <div key={t} className="flex items-center justify-between px-2 py-1 rounded text-[7px] text-zinc-500 font-sans"><span>{t}</span><div className="w-2.5 h-2.5 rounded-sm border border-zinc-700" /></div>
      ))}
      <div className="flex items-center justify-between px-2 py-1.5 rounded bg-amber-500 text-[7px] text-black font-bold font-sans ring-4 ring-amber-500/30"><span>Add to Home Screen</span><SquarePlus className="w-3 h-3" /></div>
    </div>
  </PhoneFrame>
);

const StepOpen = () => (
  <PhoneFrame>
    <div className="absolute inset-x-3 top-7 grid grid-cols-3 gap-2">
      {[...Array(5)].map((_, i) => <div key={i} className="aspect-square rounded-lg bg-zinc-800" />)}
      <div className="aspect-square rounded-lg bg-red-700 flex items-center justify-center ring-4 ring-amber-500/40 animate-pulse"><span className="font-headline text-[9px] text-white tracking-tight">DEAD</span></div>
    </div>
    <div className="absolute bottom-6 inset-x-3 rounded-lg bg-[#18181b] border border-amber-600/60 p-1.5 flex items-center gap-1.5">
      <Bell className="w-3 h-3 text-amber-500 flex-shrink-0" /><span className="text-[7px] text-zinc-300 font-sans leading-tight">Allow notifications? <b className="text-amber-400">Allow</b></span>
    </div>
  </PhoneFrame>
);

const STEPS = [
  { title: 'Tap Share', body: 'In Safari, tap the Share button at the bottom of the screen (the square with an arrow).', Art: StepShare },
  { title: 'Add to Home Screen', body: 'Scroll the list and tap "Add to Home Screen", then tap Add in the top corner.', Art: StepAdd },
  { title: 'Open from your Home Screen', body: 'Launch Deadline from the new icon, go to Profile → Notifications → Enable, and tap Allow.', Art: StepOpen },
];

// Illustrated iPhone walkthrough for installing the site and enabling push
export const InstallGuide = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const { title, body, Art } = STEPS[step];
  const last = step === STEPS.length - 1;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="install-guide">
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.22 }}
            className="w-full sm:w-[420px] bg-[#121215] border border-amber-700/50 rounded-t-2xl sm:rounded-2xl shadow-2xl p-5 relative">
            <button onClick={onClose} className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800" data-testid="install-guide-close"><X className="w-4 h-4" /></button>
            <p className="font-typewriter text-[10px] tracking-widest text-zinc-500 uppercase">iPhone · Get alerts in 3 steps</p>
            <h3 className="font-headline text-amber-500 text-xl tracking-wider mt-1">INSTALL THE DEADLINE APP</h3>

            <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }} className="mt-4">
              <Art />
              <div className="flex items-start gap-3 mt-4">
                <span className="w-7 h-7 rounded-full bg-amber-600 text-black font-headline text-sm flex items-center justify-center flex-shrink-0" data-testid="install-guide-step-number">{step + 1}</span>
                <div>
                  <p className="font-headline text-zinc-100 tracking-wide" data-testid="install-guide-step-title">{title}</p>
                  <p className="text-sm text-zinc-400 mt-0.5 leading-relaxed">{body}</p>
                </div>
              </div>
            </motion.div>

            <div className="flex items-center justify-between mt-5">
              <div className="flex gap-1.5" data-testid="install-guide-dots">
                {STEPS.map((_, i) => <button key={i} onClick={() => setStep(i)} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-amber-500' : 'w-1.5 bg-zinc-700'}`} aria-label={`Step ${i + 1}`} />)}
              </div>
              <div className="flex gap-2">
                {step > 0 && <button onClick={() => setStep((s) => s - 1)} className="px-3 py-2 rounded-lg text-zinc-400 hover:text-zinc-100 text-xs font-typewriter" data-testid="install-guide-back">Back</button>}
                <button onClick={() => (last ? onClose() : setStep((s) => s + 1))} className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-black text-xs font-headline tracking-wider flex items-center gap-1" data-testid="install-guide-next">
                  {last ? 'GOT IT' : 'NEXT'}{!last && <ChevronRight className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default InstallGuide;
